import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';
import { Reveal, RevealText } from '../components/motion/Reveal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    // Mock submission
    setTimeout(() => {
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <PageHero image="https://images.unsplash.com/photo-1764605206511-7a649d9df63b" eyebrow="Contact" title="Get in Touch" subtitle="We're here to answer your questions and arrange your perfect journey" />

      {/* Contact Methods */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white/5 text-white border-white/10 p-8 text-center group hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-all duration-300">
                <Phone className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">Call Us</h3>
              <a href="tel:03333357733" className="text-white/70 hover:text-white transition-colors">
                0333 335 7733
              </a>
              <p className="text-xs text-white/50 mt-2">24/7 Availability</p>
            </Card>

            <Card className="bg-white/5 text-white border-white/10 p-8 text-center group hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-all duration-300">
                <MessageCircle className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">WhatsApp</h3>
              <a 
                href="https://wa.me/443333357733" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-white transition-colors"
              >
                0333 335 7733
              </a>
              <p className="text-xs text-white/50 mt-2">Quick messaging</p>
            </Card>

            <Card className="bg-white/5 text-white border-white/10 p-8 text-center group hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-all duration-300">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">Email Us</h3>
              <a 
                href="mailto:info@obsidianchauffeurs.co.uk"
                className="text-white/70 hover:text-white transition-colors text-sm break-all"
              >
                info@obsidianchauffeurs.co.uk
              </a>
              <p className="text-xs text-white/50 mt-2">2-hour response time</p>
            </Card>

            <Card className="bg-white/5 text-white border-white/10 p-8 text-center group hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-all duration-300">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">Location</h3>
              <p className="text-white/70">
                Bramhall, Cheshire
              </p>
              <p className="text-xs text-white/50 mt-2">Serving UK-wide</p>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <Reveal className="lg:col-span-3">
              <Card className="bg-black text-white border-white/10 p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-2">Send Us a Message</h2>
                <p className="text-white/60 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-white mb-2">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Your name"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white mb-2">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-white mb-2">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+44 7XXX XXXXXX"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-white mb-2">Subject</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        placeholder="How can we help?"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tell us about your requirements..."
                      rows={6}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-white text-black hover:bg-white/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </Card>
            </Reveal>

            {/* Additional Info */}
            <Reveal delay={0.15} className="lg:col-span-2 space-y-6">
              {/* Quick Booking */}
              <Card className="bg-white text-black p-8">
                <h3 className="text-2xl font-bold mb-4">Need a Quick Quote?</h3>
                <p className="text-black/70 mb-6">
                  For immediate booking or quote requests, use our online booking form
                </p>
                <Link to="/booking">
                  <Button className="w-full bg-black text-white hover:bg-black/90">
                    Request a Quote
                  </Button>
                </Link>
              </Card>

              {/* Operating Hours */}
              <Card className="bg-white/5 text-white border-white/10 p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    <Clock className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold">Operating Hours</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Booking Line</span>
                    <span className="font-semibold">24/7</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Email Response</span>
                    <span className="font-semibold">Within 2 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">WhatsApp</span>
                    <span className="font-semibold">24/7</span>
                  </div>
                </div>
              </Card>

              {/* Service Areas */}
              <Card className="bg-white/5 text-white border-white/10 p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold">Service Areas</h3>
                </div>
                <div className="space-y-2 text-sm text-white/70">
                  <p>• Based in Bramhall, Cheshire</p>
                  <p>• Greater Manchester</p>
                  <p>• Liverpool & Merseyside</p>
                  <p>• Chester & North Wales</p>
                  <p>• All major UK airports</p>
                  <p>• Nationwide coverage</p>
                </div>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4"><RevealText text="Frequently Asked Questions" /></h2>
            <p className="text-xl text-white/60">Quick answers to common questions</p>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'How far in advance should I book?',
                a: 'We recommend booking at least 24 hours in advance for standard bookings. However, we also accommodate last-minute requests subject to availability. For special events or peak times, earlier booking is advisable.'
              },
              {
                q: 'What areas do you cover?',
                a: 'We are based in Bramhall, Cheshire, and provide comprehensive coverage across the UK. Our primary service area includes Greater Manchester, Cheshire, Liverpool, and surrounding regions, with nationwide coverage for long-distance journeys.'
              },
              {
                q: 'Are your chauffeurs licensed and insured?',
                a: 'Yes, all our chauffeurs are fully licensed, insured, and undergo rigorous background checks. They are professionally trained and experienced in providing premium chauffeur services.'
              },
              {
                q: 'Do you provide child seats?',
                a: 'Yes, we can provide child seats upon request. Please specify your requirements when booking, including the age and size of the child, so we can provide the appropriate seat.'
              },
              {
                q: 'What is your cancellation policy?',
                a: 'Our cancellation policy varies depending on the service type and notice period. Please contact us for specific details. We strive to be as flexible as possible to accommodate our clients\' changing needs.'
              }
            ].map((faq, index) => (
              <Reveal key={index} delay={index * 0.06}><Card className="bg-white/5 text-white border-white/10 p-6 hover:bg-white/10 transition-all duration-300" data-testid={`faq-card-${index}`}>
                <h3 className="text-lg font-bold mb-3 text-white">{faq.q}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{faq.a}</p>
              </Card></Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
